/**
 * Team Member and Invitation Models
 * Matches the Hub organization membership and invitation data models
 * Reference: /american-drone-network-hub/src/data-models/OrganizationMembership.ts
 * Reference: /american-drone-network-hub/src/data-models/OrganizationInvitation.ts
 */

/**
 * Membership role enum (bitwise flags)
 */
export enum MembershipRole {
  Pilot = 1,              // 1 << 0
  PilotInCharge = 2,      // 1 << 1
  Owner = 4,              // 1 << 2 (derived from organization.ownerProfileId)
  ChiefSupervisor = 8     // 1 << 3 (derived from organization.chiefSupervisorProfileId)
}

/**
 * Organization membership interface
 */
export interface OrganizationMembership {
  // Base fields
  id: string;
  createdAt: Date | string;
  updatedAt: Date | string;
  deletedAt?: Date | string | null;
  createdBy?: string | null;
  updatedBy?: string | null;
  deletedBy?: string | null;

  // Membership fields
  organizationId?: string;
  profileId: string;
  role: number; // Bitwise combination of MembershipRole flags
  combinedRole?: number; // Role with Owner/CS flags from organization

  // Profile details (flattened from API response)
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;

  // Legacy nested profile (for backwards compatibility)
  profile?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };

  // Computed role flags
  isOwner?: boolean;
  isChiefSupervisor?: boolean;
  isPilotInCharge?: boolean;
  isPilot?: boolean;
  isActive?: boolean;
}

/**
 * Organization invitation interface
 */
export interface OrganizationInvitation {
  // Base fields
  id: string;
  createdAt: Date | string;
  updatedAt: Date | string;

  // Invitation fields
  organizationId: string;
  email: string;
  role: number; // Bitwise combination of MembershipRole flags (no Owner/CS for user invitations)

  // Invitation state
  isAccepted: boolean;
  acceptedBy?: string | null;
  acceptedAt?: Date | string | null;
  isCancelled: boolean;
  cancelledBy?: string | null;
  cancelledAt?: Date | string | null;
  expiresAt: Date | string;

  // Reminder tracking
  firstReminderSentAt?: Date | string | null;
  finalReminderSentAt?: Date | string | null;
  lastResentAt?: Date | string | null;
  resendCount: number;

  invitedBy?: string | null;
  inviterEmail?: string | null;
  inviterName?: string | null;

  // Computed properties
  isExpired?: boolean;
  daysRemaining?: number;
  canResend?: boolean; // Based on rate limiting
}

/**
 * DTO for creating an invitation
 */
export interface CreateInvitationDto {
  email: string;
  firstName?: string;
  lastName?: string;
  role: number; // Bitwise combination (Pilot=1, PIC=2, or both=3)
}

/**
 * DTO for updating member roles
 */
export interface UpdateMemberRoleDto {
  role: number; // New bitwise role
}

/**
 * API response for invitations list
 */
export interface InvitationsListResponse {
  invitations: OrganizationInvitation[];
  stats: {
    total: number;
    pending: number;
    accepted: number;
    cancelled: number;
    expired: number;
  };
}

/**
 * API response for memberships list
 * Note: API returns 'members' (not 'memberships')
 */
export interface MembershipsListResponse {
  members: OrganizationMembership[];
  stats: {
    total: number;
    active: number;
    inactive: number;
  };
}

/**
 * Helper functions for role management
 */
export class RoleHelper {
  /**
   * Check if role has a specific flag
   */
  static hasRole(role: number, flag: MembershipRole): boolean {
    return (role & flag) === flag;
  }

  /**
   * Add a role flag
   */
  static addRole(role: number, flag: MembershipRole): number {
    return role | flag;
  }

  /**
   * Remove a role flag
   */
  static removeRole(role: number, flag: MembershipRole): number {
    return role & ~flag;
  }

  /**
   * Get role names array
   */
  static getRoleNames(role: number): string[] {
    const roles: string[] = [];
    if (this.hasRole(role, MembershipRole.Owner)) roles.push('Owner');
    if (this.hasRole(role, MembershipRole.ChiefSupervisor)) roles.push('Chief Supervisor');
    if (this.hasRole(role, MembershipRole.PilotInCharge)) roles.push('Pilot In Command');
    if (this.hasRole(role, MembershipRole.Pilot)) roles.push('Pilot');
    return roles;
  }

  /**
   * Get role badge class
   */
  static getRoleBadgeClass(roleName: string): string {
    switch (roleName) {
      case 'Owner':
        return 'badge bg-primary';
      case 'Chief Supervisor':
        return 'badge bg-success';
      case 'Pilot In Command':
        return 'badge bg-info';
      case 'Pilot':
        return 'badge bg-secondary';
      default:
        return 'badge bg-secondary';
    }
  }

  /**
   * Calculate days remaining until expiration
   */
  static getDaysRemaining(expiresAt: Date | string): number {
    const expires = new Date(expiresAt);
    const now = new Date();
    const diffMs = expires.getTime() - now.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  }

  /**
   * Check if invitation is expired
   */
  static isExpired(expiresAt: Date | string): boolean {
    return new Date(expiresAt) < new Date();
  }

  /**
   * Check if invitation can be resent (24 hour rate limit)
   */
  static canResend(lastResentAt: Date | string | null | undefined, resendCount: number): boolean {
    // Max 5 resends
    if (resendCount >= 5) return false;

    // No previous resend, can resend
    if (!lastResentAt) return true;

    // Check 24 hour rate limit
    const lastResent = new Date(lastResentAt);
    const now = new Date();
    const hoursSinceResend = (now.getTime() - lastResent.getTime()) / (1000 * 60 * 60);
    return hoursSinceResend >= 24;
  }
}
